import { useSessionStorage as useSessionStorageHook } from 'usehooks-ts';
import type { SessionStorageKey } from './types';
import { serializeSessionStorageKey } from './versioned-key';

export interface UseSessionStorageParams<T> extends SessionStorageKey {
  readonly defaultValue: T;
}

export function useSessionStorage<T>(
  args: UseSessionStorageParams<T>
): ReturnType<typeof useSessionStorageHook<T>> {
  const key = serializeSessionStorageKey(args);
  return useSessionStorageHook(key, args.defaultValue);
}

export interface WriteToSessionStorageParams<TValue> extends SessionStorageKey {
  readonly value: TValue;
}

export function writeToSessionStorage<TValue>(
  args: WriteToSessionStorageParams<TValue>
): void {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return;
  }
  const key = serializeSessionStorageKey(args);
  try {
    sessionStorage.setItem(key, JSON.stringify(args.value));
    window.dispatchEvent(new StorageEvent('session-storage', { key }));
  } catch (error) {
    console.error('Error writing to session storage', error);
  }
}

export type RemoveFromSessionStorageParams = SessionStorageKey;

export function removeFromSessionStorage(
  args: RemoveFromSessionStorageParams
): void {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return;
  }
  const key = serializeSessionStorageKey(args);
  try {
    sessionStorage.removeItem(key);
    window.dispatchEvent(new StorageEvent('session-storage', { key }));
  } catch (error) {
    console.error('Error removing from session storage', error);
  }
}

export interface ReadFromSessionStorageParams<
  TValue,
> extends SessionStorageKey {
  readonly defaultValue: TValue;
}

export function readFromSessionStorage<TValue>(
  args: ReadFromSessionStorageParams<TValue>
): TValue {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return args.defaultValue;
  }
  const key = serializeSessionStorageKey(args);
  const value = sessionStorage.getItem(key);
  if (value === null) {
    return args.defaultValue;
  }

  try {
    return JSON.parse(value) as TValue;
  } catch {
    return args.defaultValue;
  }
}
